import type { ProjectResource, RunbookSnapshotResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { isRunbookConsumerOnly } from "~/areas/projects/components/Runbooks/isRunbookConsumerOnly";
import Chip from "~/components/Chips/Chip";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { useOctopusTheme, withTheme } from "~/components/Theme";
import WarningIcon from "~/components/WarningIcon/WarningIcon";
import DateFormatter from "~/utils/DateFormatter";
import styles from "./LastPublishedChip.module.less";
export const RunbookSnapshotInformation: React.FC<{
    runbookSnapshot: RunbookSnapshotResource;
    isOutOfDate?: boolean;
}> = ({ runbookSnapshot, isOutOfDate }) => withTheme((theme) => {
    const content = runbookSnapshot && (<div className={styles.lastPublishedTooltipContent}>
                {isOutOfDate && (<>
                        <div>
                            <WarningIcon color={theme.whiteConstant} iconOverride="fa-circle-info"/> The runbook has changed since this snapshot was published
                        </div>
                        <hr />
                    </>)}
                <div>
                    Name: <strong>{runbookSnapshot.Name}</strong>
                </div>
                {runbookSnapshot.Notes && <div>{runbookSnapshot.Notes}</div>}
                <div>
                    Created: <strong>{DateFormatter.dateToShortFormat(runbookSnapshot.Assembled)}</strong>
                </div>
            </div>);
    return content;
});
RunbookSnapshotInformation.displayName = "RunbookSnapshotInformation"
export interface LastPublishedChipProps {
    project: ProjectResource;
    publishedRunbookSnapshot: RunbookSnapshotResource;
    isRunbookRunTemplateModified?: boolean;
}
export const LastPublishedChip: React.FC<LastPublishedChipProps> = ({ project, publishedRunbookSnapshot, isRunbookRunTemplateModified }) => {
    const theme = useOctopusTheme();
    if (!publishedRunbookSnapshot) {
        return null;
    }
    const showChangeSnapshotWarnings = !isRunbookConsumerOnly(project.Id);
    const isOutOfDate = isRunbookRunTemplateModified && showChangeSnapshotWarnings;
    const lastPublishedTooltip = <RunbookSnapshotInformation runbookSnapshot={publishedRunbookSnapshot} isOutOfDate={isOutOfDate}/>;
    const chipColor = isOutOfDate ? theme.info : theme.success;
    return (<InternalLink to={links.runbookSnapshotPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, runbookId: publishedRunbookSnapshot.RunbookId, runbookSnapshotId: publishedRunbookSnapshot.Id })}>
            <Chip fullWidth={true} noMargin={true} description={lastPublishedTooltip} borderColor={chipColor} labelColor={chipColor} backgroundColor={theme.whiteConstant}>
                {isOutOfDate && <WarningIcon color={theme.info} iconOverride="fa-circle-info"/>}
                Published {DateFormatter.dateToShortFormat(publishedRunbookSnapshot.Assembled)}
            </Chip>
        </InternalLink>);
};
LastPublishedChip.displayName = "LastPublishedChip"
